import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Toolbar,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { fetchAuditLogs } from '../utils/api';
import { formatLocalDateTime } from '../utils/dateTime';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../store/slices/authSlice';

const ACTION_COLORS = {
  CREATE: 'success',
  UPDATE: 'warning',
  DELETE: 'error',
  LOGIN: 'info'
};

const AuditLogs = () => {
  const user = useSelector(selectCurrentUser);
  const storedGroupName =
    user?.userGroup?.groupName ||
    (typeof window !== 'undefined' ? localStorage.getItem('userGroupName') : '') ||
    '';
  const normalizedGroupName = storedGroupName
    ?.toLowerCase?.()
    ?.replace(/[\s_-]+/g, '') || '';
  const isSuperAdmin = normalizedGroupName === 'superadmin';

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const loadLogs = useCallback(async (
    showLoading = true,
    pageNo = page,
    pageSize = rowsPerPage,
    isStale = () => false
  ) => {
    if (showLoading) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);

    try {
      const response = await fetchAuditLogs({ pageNo, pageSize });
      if (isStale()) return;
      const data = Array.isArray(response?.data?.content)
        ? response.data.content
        : Array.isArray(response?.data)
        ? response.data
        : [];
      setLogs(data.map((log) => ({
        ...log,
        updatedTimestamp: log.updatedTimestamp || log.createdTimestamp || ''
      })));

      // Extract total count from pagination metadata
      const totalElements = response?.data?.page?.totalElements || response?.data?.totalElements || data.length;
      setTotalCount(totalElements);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      if (!isStale()) setError('Failed to load audit logs. Please try again later.');
    } finally {
      if (isStale()) return;
      if (showLoading) {
        setLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    let cancelled = false;
    loadLogs(true, page, rowsPerPage, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [page, rowsPerPage, loadLogs]);

  const handleRefresh = () => {
    loadLogs(false, page, rowsPerPage);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredLogs = useMemo(() => {
    if (!searchTerm) {
      return logs;
    }

    const term = searchTerm.toLowerCase();
    return logs.filter((log) => {
      const fields = [
        log.auditId,
        log.userName,
        log.userId,
        log.action,
        log.resource,
        log.message,
        log.updatedTimestamp
      ];

      return fields.some((field) =>
        field !== undefined &&
        field !== null &&
        field.toString().toLowerCase().includes(term)
      );
    });
  }, [logs, searchTerm]);

  const renderTimestamp = (timestamp) => formatLocalDateTime(timestamp);

  if (!isSuperAdmin) {
    return (
      <Box sx={{ m: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Access Restricted
          </Typography>
          <Typography variant="body1">
            Only super-admin users can view audit logs.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ m: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Toolbar sx={{ px: 0, pb: 3, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
          <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
            Audit Logs
          </Typography>

          <TextField
            size="small"
            placeholder="Search logs"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />

          <Tooltip title="Refresh">
            <span>
              <IconButton
                color="primary"
                onClick={handleRefresh}
                disabled={loading || isRefreshing}
              >
                <RefreshIcon
                  sx={{
                    animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Toolbar>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 540 }}>
              <Table stickyHeader aria-label="audit logs table">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Resource</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLogs.length > 0 ? (
                    filteredLogs.map((log) => (
                      <TableRow key={log.auditId} hover>
                        <TableCell>{log.auditId}</TableCell>
                        <TableCell>{renderTimestamp(log.updatedTimestamp)}</TableCell>
                        <TableCell>{log.userName || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.action || 'UNKNOWN'}
                            color={ACTION_COLORS[log.action] || 'default'}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>{log.resource || '-'}</TableCell>
                        <TableCell sx={{ minWidth: 240, whiteSpace: 'normal' }}>
                          {log.message || '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No logs found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={searchTerm ? filteredLogs.length : totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AuditLogs;

